import { useState, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { toast } from 'sonner';
import {
  Button,
  Card,
  CardHeader,
  CardContent,
  CardTitle,
  CardDescription,
} from '@openzeppelin/ui-components';
import { Icon } from '@ascend/shared/components';
import { SearchBar } from '../components/ListControls';
import { CLIENT_MARGIN_ACCOUNTS, CLIENT_FACILITIES } from '../data/mockAdapter';

interface Facility {
  id: string;
  name: string;
  collateralTokens: string[];
  settlementTokens: string[];
  interestRate: string;
  maxLTV: string;
  liquidationThreshold: string;
  utilization: string;
  tvl: string;
  borrowed: string;
  status: 'active' | 'paused';
}

interface MarginAccount {
  id: string;
  facilityId: string;
  facilityName: string;
  settlementToken: string;
  collateralTokens: string[];
  collateralAmounts: Record<string, string>;
  collateralValue: string;
  borrowedAmount: string;
  borrowedValue: string;
  interestAccrued: string;
  healthFactor: string;
  status: 'healthy' | 'at-risk' | 'liquidated';
}

const FACILITIES: Facility[] = CLIENT_FACILITIES.map((facility) => ({
  id: facility.id,
  name: facility.name,
  collateralTokens: facility.collateralTokens,
  settlementTokens: facility.settlementTokens,
  interestRate: facility.interestRate,
  maxLTV: facility.maxLTV,
  liquidationThreshold: facility.liquidationThreshold,
  utilization: facility.utilization,
  tvl: facility.tvl,
  borrowed: facility.borrowed,
  status: facility.status,
}));

const MARGIN_ACCOUNTS: MarginAccount[] = CLIENT_MARGIN_ACCOUNTS.map((account) => ({
  id: account.accountId,
  facilityId: account.facilityId,
  facilityName: account.facilityName,
  settlementToken: account.settlementToken,
  collateralTokens: account.collateralTokens,
  collateralAmounts: account.collateralAmounts,
  collateralValue: account.collateralValue,
  borrowedAmount: account.borrowedAmount,
  borrowedValue: account.borrowedValue,
  interestAccrued: account.interestAccrued,
  healthFactor: account.healthFactor,
  status: account.status,
}));

const INPUT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

const SELECT_CLASS =
  'w-full rounded-md border border-input bg-background px-3 py-2 text-sm ring-offset-background focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2';

function parseAmount(s: string): number {
  return parseFloat(s.replace(/[^0-9.]/g, '')) || 0;
}

function getHFColor(hf: number): string {
  if (hf >= 1.5) return 'text-green-600';
  if (hf >= 1.2) return 'text-yellow-600';
  return 'text-red-600';
}

function getStatusColor(status: MarginAccount['status']): string {
  if (status === 'healthy') return 'bg-green-100 text-green-700';
  if (status === 'at-risk') return 'bg-yellow-100 text-yellow-700';
  return 'bg-red-100 text-red-700';
}

// Resolve query string → accountId. Prefer ?accountId; fall back to ?facilityId
// by picking the healthiest account in that facility (keeps old links working).
function resolveAccountIdFromParams(params: URLSearchParams): string | null {
  const explicit = params.get('accountId');
  if (explicit) return explicit;
  const fid = params.get('facilityId');
  if (!fid) return null;
  const candidates = MARGIN_ACCOUNTS.filter((a) => a.facilityId === fid);
  if (candidates.length === 0) return null;
  const healthy = candidates.find((a) => a.status === 'healthy');
  return (healthy ?? candidates[0]).id;
}

export function WithdrawPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const [selectedAccountId, setSelectedAccountId] = useState<string | null>(
    () => resolveAccountIdFromParams(searchParams),
  );
  const [selectedToken, setSelectedToken] = useState<string>('');
  const [amount, setAmount] = useState('');
  const [isWithdrawing, setIsWithdrawing] = useState(false);
  const [search, setSearch] = useState('');

  const account = MARGIN_ACCOUNTS.find((a) => a.id === selectedAccountId);
  const facility = account ? FACILITIES.find((v) => v.id === account.facilityId) : undefined;

  const activeToken = selectedToken || account?.collateralTokens[0] || '';
  const depositedRaw = account?.collateralAmounts[activeToken] ?? '0';
  const depositedNumeric = parseAmount(depositedRaw);
  const parsedAmount = parseFloat(amount) || 0;

  const currentHF = account ? parseFloat(account.healthFactor) : 0;
  const newHF =
    depositedNumeric > 0 && parsedAmount > 0
      ? currentHF * ((depositedNumeric - parsedAmount) / depositedNumeric)
      : currentHF;

  const filteredAccounts = useMemo(
    () =>
      MARGIN_ACCOUNTS.filter((a) => {
        const q = search.toLowerCase();
        return (
          a.facilityName.toLowerCase().includes(q) ||
          a.collateralTokens.some((t) => t.toLowerCase().includes(q))
        );
      }),
    [search],
  );

  function selectAccount(accountId: string) {
    setSelectedAccountId(accountId);
    setSearchParams({ accountId });
    setAmount('');
    setSelectedToken('');
  }

  function goBack() {
    setSelectedAccountId(null);
    setSearchParams({});
    setAmount('');
    setSelectedToken('');
  }

  function handleMax() {
    setAmount(depositedNumeric.toString());
  }

  function handleWithdraw() {
    if (parsedAmount <= 0 || parsedAmount > depositedNumeric) return;
    setIsWithdrawing(true);
    setTimeout(() => {
      setIsWithdrawing(false);
      setAmount('');
      toast.success('Withdrawal submitted successfully');
    }, 2000);
  }

  if (account && facility) {
    return (
      <div className="space-y-6">
        <button
          type="button"
          onClick={goBack}
          className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground transition-colors"
        >
          <Icon name="streamline:interface-arrows-button-left-arrow-keyboard-left" className="h-4 w-4" />
          Back to Margin Accounts
        </button>

        <Card>
          <CardHeader className="p-6">
            <CardTitle>Withdraw Collateral</CardTitle>
            <CardDescription>Withdraw collateral from your margin account ({facility.name})</CardDescription>
          </CardHeader>
          <CardContent className="p-6 pt-0 space-y-5">
            {account.collateralTokens.length > 1 && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Collateral token</label>
                <select
                  value={activeToken}
                  onChange={(e) => {
                    setSelectedToken(e.target.value);
                    setAmount('');
                  }}
                  className={SELECT_CLASS}
                >
                  {account.collateralTokens.map((t) => (
                    <option key={t} value={t}>
                      {t}: Deposited: {account.collateralAmounts[t] ?? '0'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium">Amount to withdraw</label>
                <span className="text-sm text-muted-foreground">
                  Deposited: {depositedRaw} {activeToken}
                </span>
              </div>
              <div className="relative">
                <input
                  type="number"
                  value={amount}
                  onChange={(e) => setAmount(e.target.value)}
                  placeholder="0.00"
                  min="0"
                  step="any"
                  className={`${INPUT_CLASS} pr-24`}
                />
                <div className="absolute right-1 top-1/2 -translate-y-1/2 flex items-center gap-1.5">
                  <span className="text-xs text-muted-foreground">{activeToken}</span>
                  <button
                    type="button"
                    onClick={handleMax}
                    className="rounded-md bg-primary/10 px-2 py-0.5 text-xs font-medium text-primary hover:bg-primary/20 transition-colors"
                  >
                    MAX
                  </button>
                </div>
              </div>
            </div>

            {parsedAmount > 0 && (
              <div className="rounded-lg border border-border p-4 space-y-3">
                <h4 className="text-sm font-medium">Health Factor Impact</h4>
                <div className="flex items-center gap-4 text-sm">
                  <div className="flex-1 text-center">
                    <p className="text-xs text-muted-foreground">Current</p>
                    <p className={`text-xl font-bold ${getHFColor(currentHF)}`}>
                      {currentHF.toFixed(2)}
                    </p>
                  </div>
                  <span className="text-muted-foreground text-lg">→</span>
                  <div className="flex-1 text-center">
                    <p className="text-xs text-muted-foreground">After Withdrawal</p>
                    <p
                      className={`text-xl font-bold ${getHFColor(Math.max(0, newHF))}`}
                    >
                      {Math.max(0, newHF).toFixed(2)}
                    </p>
                  </div>
                </div>
                {newHF < 1.2 && newHF >= 0 && (
                  <p className="text-xs text-red-600">
                    Warning: Health factor will be critically low after this withdrawal.
                  </p>
                )}
              </div>
            )}

            <div className="flex gap-3">
              <Button
                variant="outline"
                className="flex-1"
                onClick={goBack}
              >
                Cancel
              </Button>
              <Button
                className="flex-1"
                disabled={
                  isWithdrawing || parsedAmount <= 0 || parsedAmount > depositedNumeric
                }
                onClick={handleWithdraw}
              >
                {isWithdrawing ? (
                  <>
                    <Icon name="streamline:interface-page-controller-loading-1-progress-loading-load-wait-waiting" className="mr-2 h-4 w-4 animate-spin" />
                    Withdrawing...
                  </>
                ) : (
                  `Withdraw ${activeToken}`
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold">Withdraw Collateral</h1>
        <p className="text-sm text-muted-foreground mt-1">
          Withdraw deposited collateral from your margin accounts.
        </p>
      </div>

      <SearchBar value={search} onChange={setSearch} placeholder="Search margin accounts..." className="sm:w-64" />

      {filteredAccounts.length === 0 ? (
        <p className="text-sm text-muted-foreground py-8 text-center">No margin accounts found matching your search.</p>
      ) : (
        <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3">
          {filteredAccounts.map((acct) => {
            const v = FACILITIES.find((x) => x.id === acct.facilityId);
            if (!v) return null;

            return (
              <Card key={acct.id}>
                <CardContent className="p-6 space-y-4">
                  <div className="flex items-center justify-between">
                    <h3 className="font-semibold text-lg">{acct.facilityName}</h3>
                    <span
                      className={`rounded-full px-2.5 py-0.5 text-xs font-medium ${getStatusColor(acct.status)}`}
                    >
                      {acct.status}
                    </span>
                  </div>

                  <p className="text-sm text-muted-foreground">
                    Collateral: {acct.collateralTokens.join(', ')}
                  </p>

                  <div className="grid grid-cols-2 gap-3 text-sm">
                    <div>
                      <span className="text-muted-foreground">Collateral Value</span>
                      <p className="font-semibold">{acct.collateralValue}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Borrowed</span>
                      <p className="font-semibold">{acct.borrowedAmount}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Interest Accrued</span>
                      <p className="font-semibold text-orange-600">{acct.interestAccrued}</p>
                    </div>
                    <div>
                      <span className="text-muted-foreground">Health Factor</span>
                      <p className={`font-semibold ${getHFColor(parseFloat(acct.healthFactor))}`}>
                        {acct.healthFactor}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2 text-sm text-muted-foreground">
                    <Icon name="streamline:money-wallet-money-payment-finance-wallet" className="h-4 w-4" />
                    <span>
                      {acct.collateralTokens
                        .map((t) => `${acct.collateralAmounts[t] ?? '0'} ${t}`)
                        .join(', ')}
                    </span>
                  </div>

                  <Button
                    className="w-full"
                    onClick={() => selectAccount(acct.id)}
                  >
                    Withdraw Collateral →
                  </Button>
                </CardContent>
              </Card>
            );
          })}
        </div>
      )}
    </div>
  );
}
